import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http, formatApiError } from "../lib/api";
import { ShieldCheck, MapPin, LogIn, LogOut, Clock, Building2, RefreshCw, AlertCircle, AlertTriangle, Bell, BellOff, Home, FileText, Navigation, CalendarDays, MessageSquare, ShieldAlert, Route } from "lucide-react";
import AgentRounds from "../components/agent/AgentRounds";
import { AgentInstructions } from "../components/agent/AgentInstructions";
import { AgentFieldAlerts } from "../components/agent/AgentFieldAlerts";
import { AgentEquipment } from "../components/agent/AgentEquipment";
import { AgentAbsences } from "../components/agent/AgentAbsences";
import { PtiPanel, PtiOverlay } from "../components/agent/AgentPti";
import { usePtiController } from "../components/agent/usePtiController";
import AgentReports from "./AgentReports";
import AgentPlanning from "./AgentPlanning";
import AgentMessages from "./AgentMessages";
import { useBrand } from "../context/BrandContext";
import { InstallAppButton, useAppManifest } from "../components/InstallApp";
import { syncLocale } from "../i18n";

// Helper : convertit la clé VAPID publique (base64url) en Uint8Array pour subscribe()
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function BrandIcon({ size = "h-9 w-9", inner = "h-5 w-5" }) {
  const { name, logo_file_id } = useBrand();
  return (
    <div className={`${size} grid place-items-center overflow-hidden ${logo_file_id ? "" : "bg-sky-500"}`}>
      {logo_file_id
        ? <img src={`${BACKEND}/api/brand/logo?v=${logo_file_id}`} alt={name} className="h-full w-full object-contain"/>
        : <ShieldCheck className={`${inner} text-white`}/>}
    </div>
  );
}

function BrandName() {
  const { name } = useBrand();
  return <>{name}</>;
}

function haversine(a, b, c, d) {
  const R = 6371000, r = (x) => (x * Math.PI) / 180;
  const h = Math.sin(r(c - a) / 2) ** 2 + Math.cos(r(a)) * Math.cos(r(c)) * Math.sin(r(d - b) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Suivi GPS en continu + statut de zone pour la vacation du jour
function useGeofence(vacation) {
  const [pos, setPos] = useState(null);
  const [gpsErr, setGpsErr] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) { setGpsErr("unsupported"); return; }
    const id = navigator.geolocation.watchPosition(
      (p) => { setPos({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }); setGpsErr(null); },
      (e) => setGpsErr(e.code === 1 ? "denied" : "unavailable"),
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, []);
  const hasSiteGps = vacation && vacation.site_lat != null && vacation.site_lon != null;
  if (!hasSiteGps) return { pos, allowed: true, state: "no_site_gps" };
  if (!pos) return { pos, allowed: false, state: gpsErr ? "gps_" + gpsErr : "locating" };
  const distance = haversine(pos.lat, pos.lon, vacation.site_lat, vacation.site_lon);
  const radius = vacation.site_radius_m || 200;
  return { pos, distance, radius, allowed: distance <= radius, state: distance <= radius ? "in_zone" : "out_zone" };
}

function ZoneStatus({ geo, siteName }) {
  const map = {
    locating: ["bg-white/15 text-sky-100", "Localisation en cours…"],
    gps_denied: ["bg-rose-500/30 text-rose-50", "Localisation refusée : autorisez l'accès à votre position pour pointer."],
    gps_unavailable: ["bg-rose-500/30 text-rose-50", "Position GPS indisponible : vérifiez que la localisation est activée."],
    gps_unsupported: ["bg-rose-500/30 text-rose-50", "Ce téléphone ne permet pas la localisation."],
    in_zone: ["bg-emerald-500/30 text-emerald-50", `Dans la zone de « ${siteName} » (${Math.round(geo.distance)} m / ${geo.radius} m)`],
    out_zone: ["bg-rose-500/30 text-rose-50", `Hors zone : vous êtes à ${Math.round(geo.distance)} m de « ${siteName} » (rayon ${geo.radius} m). Rapprochez-vous pour pointer.`],
  };
  const m = map[geo.state];
  if (!m) return null;
  return <div data-testid="zone-status" data-state={geo.state} className={`mb-3 text-xs rounded-md px-3 py-2 flex items-start gap-1.5 ${m[0]}`}><Navigation className="h-3.5 w-3.5 mt-0.5 shrink-0" />{m[1]}</div>;
}

function fmtTime(iso) { return new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
function fmtDate(iso) { return new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long" }); }

export default function AgentApp() {
  const [agent, setAgent] = useState(null);
  const [ready, setReady] = useState(false);
  const [vacations, setVacations] = useState([]);
  const [history, setHistory] = useState([]);

  const { refresh: refreshBrand } = useBrand();
  const refresh = async () => {
    try {
      const me = await http.get("/agent-app/me");
      setAgent(me.data);
      syncLocale();
      const [v, h] = await Promise.all([http.get("/agent-app/vacations?days=14"), http.get("/agent-app/check-ins?limit=10")]);
      setVacations(v.data); setHistory(h.data);
    } catch { setAgent(null); }
    finally { setReady(true); refreshBrand(); }
  };
  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    const end = (e) => { setAgent(null); toast.error(e.detail || "Session terminée : reconnectez-vous"); };
    window.addEventListener("agent-session-ended", end);
    return () => window.removeEventListener("agent-session-ended", end);
  }, []);

  if (!ready) return <div className="min-h-screen grid place-items-center bg-[#061A55] text-[#E8C46A]">Chargement…</div>;
  if (!agent) return <><AgentManifest name="" /><AgentLogin onLogin={refresh}/></>;

  return <><AgentManifest name={agent.first_name} /><AgentHome agent={agent} vacations={vacations} history={history} onRefresh={refresh} onLogout={async()=>{ try { await http.post("/agent-app/logout"); } catch { /* session déjà close */ } setAgent(null); }}/></>;
}

function AgentManifest({ name }) {
  useAppManifest({ kind: "agent", name: name || "" });
  return null;
}

function AgentLogin({ onLogin }) {
  const [company, setCompany] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    return (q.get("societe") || q.get("company") || localStorage.getItem("xaltix_agent_company") || "").trim().toLowerCase();
  });
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const c = company.trim().toLowerCase();
      await http.post("/agent-app/login", { company: c, email, pin });
      localStorage.setItem("xaltix_agent_company", c);
      onLogin();
    }
    catch (e2) { setErr(formatApiError(e2)); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex flex-col bg-[radial-gradient(900px_500px_at_50%_-10%,rgba(232,196,106,.18),transparent_60%),linear-gradient(180deg,#0B2A7A,#061A55)] text-white p-6">
      <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-sm">
          <div className="flex flex-col items-center mb-10">
            <div className="mb-5 rounded-3xl overflow-hidden bg-white p-2 ring-1 ring-[#E8C46A]/60 shadow-[0_20px_40px_-20px_rgba(0,0,0,.6)]">
              <BrandIcon size="h-40 w-40 rounded-3xl" inner="h-20 w-20" />
            </div>
            <div className="font-display text-3xl font-extrabold tracking-tight" data-testid="agent-brand-name"><BrandName/></div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E8C46A] font-bold mt-1">Agent</div>
          </div>
          <form onSubmit={submit} className="space-y-4" data-testid="agent-login-form">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">Code société</label>
              <input data-testid="agent-company" autoComplete="organization" autoCapitalize="none" value={company} onChange={(e)=>setCompany(e.target.value)} required
                className="mt-1 w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#E8C46A]"/>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">Email</label>
              <input data-testid="agent-email" type="email" autoComplete="email" value={email} onChange={(e)=>setEmail(e.target.value)} required
                className="mt-1 w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-base text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#E8C46A]"/>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#E8C46A]">Code PIN</label>
              <input data-testid="agent-pin" type="password" inputMode="numeric" pattern="[0-9]*" value={pin} onChange={(e)=>setPin(e.target.value)} required
                className="mt-1 w-full bg-white/10 border border-white/20 rounded-lg px-3 py-3 text-base text-white text-center tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-[#E8C46A]"/>
            </div>
            {err && <div className="text-sm text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="agent-login-submit" disabled={loading} className="w-full bg-gradient-to-br from-[#E8C46A] to-[#C9962E] hover:brightness-105 active:scale-[.98] text-[#0B1A4A] font-bold rounded-lg py-3.5 text-base disabled:opacity-70 transition-all">{loading ? "Connexion…" : "Se connecter"}</button>
          </form>
          <p className="text-center text-xs text-[#C9D6F5] mt-8">Demandez votre code société et votre code PIN à votre administrateur.</p>
          <div className="mt-5 flex justify-center"><InstallAppButton dark /></div>
          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-[#C9D6F5]/80 mt-10" data-testid="agent-xaltix-mention"><span className="font-[Michroma] tracking-[0.3em] text-[#E8C46A]">XALTIX</span> · par Solutions PRIME</p>
        </div>
      </div>
    </div>
  );
}

function CardExpiryBanner({ cnaps }) {
  const vu = cnaps?.status === "valid" ? cnaps.valid_until : null;
  if (!vu) return null;
  const days = Math.round((new Date(`${vu}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
  if (days > 60) return null;
  const tone = days <= 7 ? "bg-rose-600 text-white border-rose-700" : days <= 30 ? "bg-orange-500 text-white border-orange-600" : "bg-yellow-300 text-yellow-950 border-yellow-400";
  return (
    <div data-testid="agent-card-expiry" className={`rounded-xl border-2 px-4 py-3 shadow ${tone}`}>
      <div className="font-extrabold text-sm">{days < 0 ? "Votre carte professionnelle est expirée" : `Votre carte professionnelle expire dans ${days} jour${days > 1 ? "s" : ""}`}</div>
      <div className="text-xs mt-0.5 opacity-90">Valide jusqu&apos;au {vu.split("-").reverse().join("/")}. Lancez dès maintenant votre demande de renouvellement auprès du CNAPS, puis prévenez votre responsable.</div>
    </div>
  );
}

function AgentHome({ agent, vacations, history, onRefresh, onLogout }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [pushState, setPushState] = useState("checking"); // "on" | "off" | "unsupported" | "denied"
  const [tab, setTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") || "home"));
  const [msgUnread, setMsgUnread] = useState(0);
  const pti = usePtiController();
  useEffect(() => {
    const f = () => http.get("/agent-app/messages").then((r) => setMsgUnread(r.data.reduce((n, c) => n + c.unread, 0))).catch(() => {});
    if (tab === "messages") { setMsgUnread(0); return undefined; }
    f(); const iv = setInterval(f, 60000); return () => clearInterval(iv);
  }, [tab]);
  const today = new Date().toISOString().slice(0, 10);
  const todays = vacations.filter(v => v.date === today);
  const upcoming = vacations.filter(v => v.date > today);

  const lastCheck = history[0];
  const isCheckedIn = lastCheck?.kind === "in";
  const geo = useGeofence(todays[0]);

  useEffect(() => {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) { setPushState("unsupported"); return; }
    if (Notification.permission === "denied") { setPushState("denied"); return; }
    navigator.serviceWorker.ready.then(reg => reg.pushManager.getSubscription()).then(s => setPushState(s ? "on" : "off")).catch(() => setPushState("off"));
  }, []);

  const enablePush = async () => {
    try {
      if (Notification.permission !== "granted") {
        const p = await Notification.requestPermission();
        if (p !== "granted") { setPushState("denied"); return; }
      }
      const reg = await navigator.serviceWorker.ready;
      const { data: keyResp } = await http.get("/agent-app/push-key");
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(keyResp.public_key) });
      await http.post("/agent-app/push-subscribe", { endpoint: sub.endpoint, keys: { p256dh: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("p256dh")))).replace(/\+/g,"-").replace(/\//g,"_").replace(/=+$/,""), auth: btoa(String.fromCharCode(...new Uint8Array(sub.getKey("auth")))).replace(/\+/g,"-").replace(/\//g,"_").replace(/=+$/,"") } });
      setPushState("on");
      setMsg("Notifications activées ✓"); setTimeout(()=>setMsg(""), 2500);
    } catch (e) { setMsg("Erreur: " + (e?.message || "activation impossible")); setTimeout(()=>setMsg(""), 3500); }
  };

  const disablePush = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await http.post("/agent-app/push-unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setPushState("off");
    } catch {}
  };

  const doCheck = async (kind, vacationId) => {
    if (kind === "out") {
      const open = (await http.get("/agent-app/keys").catch(() => ({ data: [] }))).data.filter((k) => k.loan && k.loan.lent_by === agent.id);
      if (open.length && !window.confirm(`Prêts en cours : ${open.map((k) => `${k.label} (${k.loan.borrower_name})`).join(", ")}.\nPensez à les récupérer ou à prévenir la relève. Pointer le départ quand même ?`)) return;
    }
    setBusy(true); setMsg("");
    try {
      const coords = geo.pos ? geo.pos : await new Promise((res) => {
        if (!navigator.geolocation) return res({});
        navigator.geolocation.getCurrentPosition(
          (p) => res({ lat: p.coords.latitude, lon: p.coords.longitude, accuracy: p.coords.accuracy }),
          () => res({}),
          { enableHighAccuracy: true, timeout: 8000 }
        );
      });
      const r = await http.post("/agent-app/check", { kind, vacation_id: vacationId || null, ...coords });
      if (r.data?.off_site) {
        setMsg(`⚠ Pointage enregistré HORS ZONE (à ${r.data.distance_m?.toFixed(0)} m du site, rayon ${r.data.radius_m} m). Votre administrateur a été alerté.`);
      } else if (r.data?.distance_m != null) {
        setMsg(`${kind === "in" ? "Arrivée" : "Départ"} pointée ✓ (à ${r.data.distance_m.toFixed(0)} m du site)`);
      } else {
        setMsg(kind === "in" ? "Pointage d'arrivée enregistré ✓" : "Pointage de départ enregistré ✓");
      }
      await onRefresh();
    } catch (e) { setMsg(formatApiError(e)); }
    finally { setBusy(false); setTimeout(()=>setMsg(""), 4500); }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="bg-[linear-gradient(160deg,#061A55,#0B2A7A)] text-white px-5 pt-6 pb-8 rounded-b-3xl shadow-xl border-b-2 border-[#C9962E]">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="rounded-lg bg-white p-0.5 ring-1 ring-[#E8C46A]/60"><BrandIcon size="h-9 w-9 rounded-md" inner="h-5 w-5" /></div>
            <div>
              <div className="font-display text-base font-extrabold leading-none"><BrandName/></div>
              <div className="text-[9px] uppercase tracking-[0.2em] text-[#E8C46A] mt-0.5 font-bold">Agent</div>
            </div>
          </div>
          <div className="flex gap-2">
            {pushState === "off" && <button data-testid="enable-push" onClick={enablePush} title="Activer les notifications" className="p-2 rounded-lg bg-gradient-to-br from-[#E8C46A] to-[#C9962E] text-[#0B1A4A]"><Bell className="h-4 w-4"/></button>}
            {pushState === "on" && <button onClick={disablePush} title="Désactiver les notifications" className="p-2 rounded-lg bg-emerald-500/30 hover:bg-emerald-500/50"><Bell className="h-4 w-4"/></button>}
            {pushState === "denied" && <button title="Notifications bloquées par le navigateur" disabled className="p-2 rounded-lg bg-rose-500/30 opacity-60"><BellOff className="h-4 w-4"/></button>}
            <button onClick={onRefresh} className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><RefreshCw className="h-4 w-4"/></button>
            <button data-testid="agent-logout" onClick={onLogout} className="p-2 rounded-lg bg-white/10 hover:bg-white/20"><LogOut className="h-4 w-4"/></button>
          </div>
        </div>
        <div>
          <div className="text-xs text-[#E8C46A] uppercase tracking-widest font-bold">Bonjour</div>
          <h1 className="font-display text-3xl font-extrabold leading-tight">{agent.first_name} {agent.last_name}</h1>
          <div className="text-xs text-[#C9D6F5] mt-1 capitalize">{fmtDate(new Date().toISOString())}</div>
        </div>

        {tab !== "pti" && tab !== "rounds" && <>
        <div className="mt-5 bg-white/10 backdrop-blur rounded-xl p-4 border border-[#E8C46A]/30">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[10px] uppercase tracking-widest text-[#E8C46A] font-bold">Statut</div>
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-widest ${isCheckedIn ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-700"}`}>{isCheckedIn ? "En service" : "Hors service"}</span>
          </div>
          <ZoneStatus geo={geo} siteName={todays[0]?.site_name || "site"} />
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="checkin" disabled={busy || isCheckedIn || !geo.allowed} onClick={()=>doCheck("in", todays[0]?.id)} className="flex flex-col items-center gap-1 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl py-4 active:scale-95 transition-all">
              <LogIn className="h-6 w-6"/><span className="text-sm font-bold">Pointer arrivée</span>
            </button>
            <button data-testid="checkout" disabled={busy || !isCheckedIn || !geo.allowed} onClick={()=>doCheck("out", todays[0]?.id)} className="flex flex-col items-center gap-1 bg-rose-500 hover:bg-rose-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl py-4 active:scale-95 transition-all">
              <LogOut className="h-6 w-6"/><span className="text-sm font-bold">Pointer départ</span>
            </button>
          </div>
          {msg && <div className="mt-3 text-xs text-center bg-white/15 rounded-md py-2 animate-fade-in-up whitespace-pre-line">{msg}</div>}
        </div>
        <div className="mt-4 flex justify-center"><InstallAppButton dark /></div>
        </>}
      </header>

      <main className="px-5 mt-4 space-y-6">
        <CardExpiryBanner cnaps={agent?.cnaps} />
        {tab === "pti" ? <PtiPanel pti={pti} /> : tab === "rounds" ? <AgentRounds /> : tab === "reports" ? <AgentReports off={agent.modules_off || []} /> : tab === "planning" ? <><AgentAbsences /><AgentPlanning /></> : tab === "messages" ? <AgentMessages /> : <>
        <AgentFieldAlerts />
        <AgentEquipment />
        {!agent.modules_off?.includes("consignes") && <AgentInstructions />}
        <section>
          <h2 className="font-display text-lg font-extrabold mb-2 text-[#0B2A7A] after:block after:h-0.5 after:w-10 after:mt-1 after:bg-gradient-to-r after:from-[#C9962E] after:to-transparent">Aujourd'hui</h2>
          {todays.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500">Aucune vacation prévue aujourd'hui.</div>
          ) : (
            <div className="space-y-2">{todays.map(v => <VacationItem key={v.id} v={v}/>)}</div>
          )}
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold mb-2 text-[#0B2A7A] after:block after:h-0.5 after:w-10 after:mt-1 after:bg-gradient-to-r after:from-[#C9962E] after:to-transparent">À venir (14j)</h2>
          {upcoming.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-5 text-center text-sm text-slate-500">Aucune vacation à venir.</div>
          ) : (
            <div className="space-y-2">{upcoming.slice(0, 8).map(v => <VacationItem key={v.id} v={v}/>)}</div>
          )}
        </section>

        <section>
          <h2 className="font-display text-lg font-extrabold mb-2 text-[#0B2A7A] after:block after:h-0.5 after:w-10 after:mt-1 after:bg-gradient-to-r after:from-[#C9962E] after:to-transparent">Mes derniers pointages</h2>
          <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="checkin-history">
            {history.length === 0 && <div className="p-5 text-center text-sm text-slate-500">Aucun pointage.</div>}
            {history.map(h => (
              <div key={h.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-9 w-9 rounded-lg grid place-items-center text-white flex-shrink-0 ${h.kind==="in"?"bg-emerald-500":"bg-rose-500"}`}>
                    {h.kind==="in" ? <LogIn className="h-4 w-4"/> : <LogOut className="h-4 w-4"/>}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-1.5">
                      {h.kind==="in"?"Arrivée":"Départ"}
                      {h.off_site && <span className="inline-flex items-center gap-0.5 bg-rose-100 text-rose-800 border border-rose-300 rounded text-[9px] font-bold uppercase tracking-widest px-1 py-0.5"><AlertTriangle className="h-2.5 w-2.5"/>Hors zone</span>}
                    </div>
                    <div className="text-xs text-slate-500">{new Date(h.at).toLocaleString("fr-FR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}{h.distance_m != null && ` · ${h.distance_m.toFixed(0)} m`}</div>
                  </div>
                </div>
                {h.lat && h.lon && (
                  <a href={`https://www.openstreetmap.org/?mlat=${h.lat}&mlon=${h.lon}#map=18/${h.lat}/${h.lon}`} target="_blank" rel="noreferrer" className="text-xs text-sky-700 hover:underline inline-flex items-center gap-1 flex-shrink-0"><MapPin className="h-3 w-3"/>GPS</a>
                )}
              </div>
            ))}
          </div>
        </section>
        </>}
      </main>
      <nav className="fixed bottom-0 inset-x-0 bg-[linear-gradient(180deg,#0B2A7A,#061A55)] border-t-2 border-[#C9962E] grid grid-cols-6 z-40 pb-[env(safe-area-inset-bottom)]" data-testid="agent-bottom-nav">
        {[["home", "Accueil", Home], ["planning", "Planning", CalendarDays], ["pti", "PTI", ShieldAlert], ["rounds", "Rondes", Route], ["reports", "Rapports", FileText], ["messages", "Messages", MessageSquare]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`agent-tab-${k}`} onClick={() => setTab(k)} className={`flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-bold transition-colors ${tab === k ? "text-[#E8C46A]" : "text-[#C9D6F5]/70 hover:text-white"}`}>
            <span className="relative"><Icon className="h-5 w-5" />{k === "pti" && pti.active && <span data-testid="agent-tab-pti-dot" className="absolute -top-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-emerald-400 ring-2 ring-[#0B2A7A]" />}{k === "messages" && msgUnread > 0 && <span data-testid="agent-tab-messages-dot" className="absolute -top-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-rose-600 ring-2 ring-[#0B2A7A]" />}</span>{l}
          </button>
        ))}
      </nav>
      <PtiOverlay pti={pti} />
    </div>
  );
}

function VacationItem({ v }) {
  const cls = { regular:"border-l-slate-300", standard:"border-l-slate-300", night:"border-l-slate-900", holiday:"border-l-amber-500", sunday:"border-l-indigo-500", unassigned:"border-l-rose-500" }[v.shift_type] || "border-l-slate-300";
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${cls} rounded-lg p-3 flex items-start justify-between`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <Clock className="h-3.5 w-3.5 text-slate-400"/>
          <span className="text-sm font-bold">{v.start_time} – {v.end_time}</span>
          <span className="text-[10px] text-slate-500">{new Date(v.date).toLocaleDateString("fr-FR", { weekday:"short", day:"2-digit", month:"short" })}</span>
        </div>
        <div className="flex items-center gap-1.5 text-sm text-slate-700">
          <Building2 className="h-3.5 w-3.5 text-slate-400"/>
          <span className="font-semibold truncate">{v.site_name || "Site"}</span>
        </div>
        {v.site_address && <div className="text-xs text-slate-500 ml-5 truncate">{v.site_address}</div>}
      </div>
    </div>
  );
}
